import React, { useCallback, useEffect, useRef, useState } from 'react'
import { reviewApi } from '../lib/reviewApi'
import { useReviewRequest } from '../lib/useReviewRequest'
import GuidedSenderSetup from './GuidedSenderSetup'

const empty = { domain: '', from_email: '', reply_to: '', review_url: '' }
const states = { not_started: 'Not started', pending: 'Sender details saved', verified: 'Sender verified', expired: 'Verification expired', failed: 'Verification needs attention', revoked: 'Sender withdrawn' }

// Mounted only inside the identity-keyed owner Settings form. No provider secrets
// or verification claims are accepted from this component.
export default function ReviewSenderSetup({ workshopId, onChanged }) {
  const ticket = useReviewRequest(), pending = useRef(null), inFlight = useRef(false)
  const [data, setData] = useState(null), [form, setForm] = useState(empty)
  const [busy, setBusy] = useState(false), [error, setError] = useState('')
  const load = useCallback(async () => {
    const current = ticket()
    setBusy(true); setError(''); setData(null); pending.current = null
    try {
      const result = await reviewApi(workshopId, 'sender_setup_read')
      if (!current()) return
      if (result.workshop_id !== workshopId || !Number.isInteger(result.version) || !states[result.state]) throw new Error('Sender setup is unavailable. Reload before making changes.')
      setData(result); setForm(Object.fromEntries(Object.keys(empty).map(k => [k, result[k] || ''])))
    } catch (e) { if (current()) setError(e.message) }
    finally { if (current()) setBusy(false) }
  }, [workshopId, ticket])
  useEffect(() => { load() }, [load])
  function edit(key, value) {
    pending.current = null
    setForm(f => ({ ...f, [key]: value })); setError('')
  }
  async function save(action) {
    if (inFlight.current || !data) return
    const command = action === 'save' ? { action, ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()])) } : { action }
    const fingerprint = JSON.stringify([data.version, command])
    if (pending.current?.fingerprint !== fingerprint) pending.current = { fingerprint, request_id: crypto.randomUUID() }
    const request = { request_id: pending.current.request_id, expected_version: data.version, command }
    const current = ticket()
    inFlight.current = true; setBusy(true); setError('')
    try {
      const result = await reviewApi(workshopId, 'sender_setup_write', request)
      if (!current()) return
      if (result.workshop_id !== workshopId || !Number.isInteger(result.version)) throw new Error('Saved status could not be verified. Retry or reload.')
      setData(result); setForm(Object.fromEntries(Object.keys(empty).map(k => [k, result[k] || '']))); pending.current = null
      onChanged()
    } catch (e) { if (current()) setError(`${e.message} The result may have been saved. Retry unchanged details or reload saved status.`) }
    finally { inFlight.current = false; if (current()) setBusy(false) }
  }
  return <section className="review-notice" aria-label="Workshop sender setup">
    <h3>Set up your workshop sender</h3>
    {data?.state!=='verified'&&<p>Use your workshop’s own sending domain and monitored reply address.</p>}
    {error && <p role="alert">{error}</p>}
    {!data && !error && <p role="status">Loading sender setup…</p>}
    {data && <>
      {data.state!=='verified'&&<p role="status"><strong>{states[data.state]}</strong></p>}
      {data.state==='revoked'&&<p>Sending is off. Save your details and begin a new guided verification before reuse.</p>}
      {data.state==='failed'&&<p>{data.next_step}</p>}
      <details key={data.state==='verified'?'complete':'incomplete'} open={data.state!=='verified'}>
      <summary>Sender details{data.state==='verified'?' · '+data.from_email:''}</summary>
      <form onSubmit={e => { e.preventDefault(); save('save') }}>
        <fieldset disabled={busy} style={{ border: 0, padding: 0, minWidth: 0 }}>
          <div className="field"><label htmlFor="sender-domain">Sending domain</label><input id="sender-domain" required maxLength={253} value={form.domain} onChange={e => edit('domain', e.target.value)} placeholder="reviews.yourworkshop.co.uk" /></div>
          <div className="field"><label htmlFor="sender-from">From email address</label><input id="sender-from" type="email" required maxLength={254} value={form.from_email} onChange={e => edit('from_email', e.target.value)} placeholder="hello@reviews.yourworkshop.co.uk" /></div>
          <div className="field"><label htmlFor="sender-reply">Monitored Reply-To email address</label><input id="sender-reply" type="email" required maxLength={254} value={form.reply_to} onChange={e => edit('reply_to', e.target.value)} placeholder="service@yourworkshop.co.uk" /></div>
          <div className="field"><label htmlFor="sender-google">Google review link for this setup</label><input id="sender-google" type="url" required value={form.review_url} onChange={e => edit('review_url', e.target.value)} /></div>
          <p>The From mailbox must use this domain and receive a code. Saving changes withdraws verification and switches invitations off.</p>
          <button type="submit" className="btn btn-primary">{busy ? 'Saving…' : 'Save sender setup'}</button>
        </fieldset>
      </form>
      {data.version > 0 && <div className="review-actions">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => save('request_review')}>Request fresh verification</button>
        <button type="button" className="btn btn-secondary" disabled={busy || data.state === 'revoked'} onClick={() => save('revoke')}>Withdraw sender setup</button>
      </div>}
      <p>Requesting new authorization or withdrawing setup switches invitations off. No shared sender is substituted.</p>
      </details>
      {data.version > 0 && data.state !== 'revoked' && <GuidedSenderSetup key={workshopId + ':' + data.version} workshopId={workshopId} version={data.version} onStatusChanged={onChanged} />}
    </>}
    <button type="button" className="btn btn-secondary" disabled={busy} onClick={()=>{load();onChanged()}}>Reload saved sender status</button>
  </section>
}
